import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { AESEncryptDecryptService } from './aesencrypt-decrypt.service';
import { UtilsService } from './utils.service';
import { CloverGoProvisioningService } from './payment-terminal/clover-go-provisioning.service';
import { RunnerPrintRelayService } from './runner-print-relay.service';
import { DeviceIdentityService } from './device-identity.service';

// localStorage key used to hand a provisioned activation code from the
// APP_INITIALIZER (where Router.navigate is risky) to ngOnInit of
// AppComponent (where the router is fully settled).
export const NATIVE_BOOT_CODE_KEY = '__native_boot_code';
export const NATIVE_BOOT_SESSION_KEY = '__native_boot_session';

// Capacitor Preferences key where Driven MDM pushes the per-device
// activation code as managed config. Never committed — MDM writes it.
const PROVISIONED_CODE_KEY = 'device_activation_code';

@Injectable({ providedIn: 'root' })
export class DeviceModeBootService {
  constructor(
    private aes: AESEncryptDecryptService,
    private utils: UtilsService,
    private cloverGoProvisioning: CloverGoProvisioningService,
    private runnerPrintRelay: RunnerPrintRelayService,
    private deviceIdentity: DeviceIdentityService,
  ) {}

  async boot(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    // Seed the non-secret Clover Go device config (app id + Clover env) so the
    // Go reader is ready to pair the moment its MDM-pushed key/secret/token
    // arrive. Idempotent and MDM-first — never overwrites an existing value.
    await this.cloverGoProvisioning.seedFromEnvironment();

    // Always arm device mode on native. The URL-based arm inside
    // login-code.component.ts requires ?isDevice=true in query params, which
    // never appear on a native cold boot (capacitor://localhost/index.html).
    // Setting it here ensures the sidebar shows Lock (not Logout) on every
    // launch, including after a live JWT restore that skips /login-code.
    localStorage.setItem('isDeviceMode', 'true');

    // Bridge a historical Preferences key inconsistency:
    //   login-code.component.ts writes encrypted session to key 'user'
    //   login.component.ts's capacitorStoageSet() reads from key 'currentUser'
    // Copying across on every native boot means the restore path finds the
    // session regardless of which component last activated the device.
    await this.bridgePreferencesKeys();

    const savedUser = await this.readSavedUser();
    if (savedUser) {
      await this.activateSession(savedUser);
      return;
    }

    // No saved session: try the same managed-hardware activation contract as
    // DineNGo Assistant. If Hardware Devices contains this Android serial and
    // its activation password is the same serial, the backend returns a
    // long-lived device JWT and the register opens without showing login.
    // Unregistered/unsupported devices quietly retain the normal login UX.
    const deviceUser = await this.deviceIdentity.authenticateRegisteredDevice();
    if (deviceUser?.token) {
      await this.persistSession(deviceUser);
      await this.activateSession(deviceUser);
      return;
    }

    // No session. Check for a provisioned code pushed by Driven MDM as managed
    // config (key 'device_activation_code' in Capacitor Preferences). Stage it
    // in localStorage so AppComponent.ngOnInit() can pick it up and navigate
    // to /login-code for silent auto-activation on first boot or after a wipe.
    const provisioned = await Preferences.get({ key: PROVISIONED_CODE_KEY });
    if (provisioned?.value) {
      localStorage.setItem(NATIVE_BOOT_CODE_KEY, provisioned.value);
    }
    // No provisioned code → app loads normally and shows the login-code entry
    // form, which is the correct first-time-setup UX for a fresh enrollment.
  }

  private async bridgePreferencesKeys(): Promise<void> {
    const fromUser = await Preferences.get({ key: 'user' });
    const fromCurrentUser = await Preferences.get({ key: 'currentUser' });
    if (fromUser?.value && !fromCurrentUser?.value) {
      await Preferences.set({ key: 'currentUser', value: fromUser.value });
    }
  }

  private async readSavedUser(): Promise<any | null> {
    const entry = await Preferences.get({ key: 'currentUser' });
    if (!entry?.value) { return null; }
    try {
      const decrypted = this.aes.decrypt(entry.value);
      return decrypted ? JSON.parse(decrypted) : null;
    } catch {
      return null;
    }
  }

  private async persistSession(user: any): Promise<void> {
    const encrypted = this.aes.encrypt(JSON.stringify(user));
    await Promise.all([
      Preferences.set({ key: 'user', value: encrypted }),
      Preferences.set({ key: 'currentUser', value: encrypted }),
    ]);
  }

  private async activateSession(user: any): Promise<void> {
    const role = user.selectedRole || user.role?.[0];
    this.utils.setCurrentUser(user, role);
    await this.cloverGoProvisioning.seedMerchant(
      this.cloverGoProvisioning.merchantIdFromUser(user),
    );
    await this.cloverGoProvisioning.seedAccessToken();
    void this.runnerPrintRelay.start();
    localStorage.setItem(NATIVE_BOOT_SESSION_KEY, 'true');
  }
}

export function deviceModeBootFactory(svc: DeviceModeBootService): () => Promise<void> {
  return () => svc.boot();
}
